import GeoJSON from "ol/format/GeoJSON.js";
import {
  reprojectFeature,
  reprojectPointAsCoords,
  convertTo4326
} from "utils/OpenLayers/TransformUtils";
import { olCircleInMeters } from "utils/OpenLayers/OLLayerUtils";
import { Point } from "ol/geom";
import Feature from "ol/Feature";

/**
 * Adds the project points, adding a "ghost" type if needed for later
 * change the style.
 * @param {Object} points - Object containing start and/or end points
 * @param {Object} sources - OpenLayers sources for start and end points
 * @param {String} type - Type of the point ("ghost" or other)
 */
export function updateProjectPointsWithType(points, sources, type = "ghost") {
  const format = new GeoJSON();
  if (points.start && sources.start) {
    const startP = reprojectFeature(format.readFeature(points.start));
    if (type === "ghost") startP.set("type", type);
    sources.start.addFeature(startP);
  }
  if (points.end && sources.end) {
    const endP = reprojectFeature(format.readFeature(points.end));
    if (type === "ghost") endP.set("type", type);
    sources.end.addFeature(endP);
  }
}

/**
 * Adds project and scenario points to the map, clearing existing points first.
 * Prioritizes scenario points unless in edit mode, then uses project points.
 *
 * @param {Object} points - Object containing project and scenario points
 * @param {Object} points.project - Project points
 * @param {Object} points.project.start - GeoJSON start point for project
 * @param {Object} points.project.end - GeoJSON end point for project
 * @param {Object} points.scenario - Scenario points
 * @param {Object} points.scenario.start - GeoJSON start point for scenario
 * @param {Object} points.scenario.end - GeoJSON end point for scenario
 * @param {Object} sources - Object containing OpenLayers sources
 * @param {Object} sources.project - Project point sources
 * @param {Object} sources.project.start - OpenLayers source for project start point
 * @param {Object} sources.project.end - OpenLayers source for project end point
 * @param {Object} sources.scenario - Scenario point sources
 * @param {Object} sources.scenario.start - OpenLayers source for scenario start point
 * @param {Object} sources.scenario.end - OpenLayers source for scenario end point
 * @param {boolean} isEditMode - If true, prioritizes project points over scenario points
 */
export function loadProjectPoints(points, sources, isEditMode) {
  // Clear all existing points from project and scenario sources
  sources.project?.start?.clear(true);
  sources.project?.end?.clear(true);
  sources.scenario?.start?.clear(true);
  sources.scenario?.end?.clear(true);

  // Initialize GeoJSON format for feature conversion
  const format = new GeoJSON();

  // Handle start point: prioritize scenario start point unless in edit mode
  if (points.scenario.start && !isEditMode) {
    // Convert scenario start point to feature and set properties
    const startP = reprojectFeature(format.readFeature(points.scenario.start));
    startP.set("name", "StartPoint");
    startP.set("domain", "scenario");
    // Add scenario start point to its source
    sources.scenario.start.addFeature(startP);
  } else if (points.project.start) {
    // Convert project start point to feature and set properties
    const startP = reprojectFeature(format.readFeature(points.project.start));
    startP.set("name", "StartPoint");
    startP.set("domain", "project");
    // Add project start point to its source
    sources.project.start.addFeature(startP);
  }

  // Handle end point: prioritize scenario end point unless in edit mode
  if (points.scenario.end && !isEditMode) {
    // Convert scenario end point to feature and set properties
    const endP = reprojectFeature(format.readFeature(points.scenario.end));
    endP.set("name", "EndPoint");
    endP.set("domain", "scenario");
    // Add scenario end point to its source
    sources.scenario.end.addFeature(endP);
  } else if (points.project.end) {
    // Convert project end point to feature and set properties
    const endP = reprojectFeature(format.readFeature(points.project.end));
    endP.set("name", "EndPoint");
    endP.set("domain", "project");
    // Add project end point to its source
    sources.project.end.addFeature(endP);
  }
}

/**
 * Adds the project area to the map, clearing any previous area first.
 * @param {Object} area - GeoJSON area object
 * @param {Object} source - OpenLayers source for project area
 */
export function loadProjectArea(area, source) {
  // Remove any previous project area from areaSource
  source.clear(true);

  // Add new project area feature to areaSource
  if (area) {
    const projectedArea = reprojectFeature(new GeoJSON().readFeature(area));
    source.addFeature(projectedArea);
  }
}

/**
 * Adds the scenario intermediate points to the map, clearing any
 * previous intermediate points first.
 * @param {Object} points - GeoJSON for scenario intermediate points
 * @param {Object} source  - OpenLayers sources for intermediate points
 */
export function loadIntermediatePoints(points, source) {
  source.clear(true);

  const format = new GeoJSON();
  const iPoints = format.readFeature(points);
  iPoints
    .getGeometry()
    .getPoints()
    .forEach((item, index) => {
      const pointFeature = format.readFeature({
        type: "Point",
        coordinates: item.getCoordinates()
      });
      pointFeature.set("markerID", (index + 1).toString());
      source.addFeature(reprojectFeature(pointFeature));
    });
}

/**
 * Adds a start point to the specified OpenLayers source.
 * @param {Object} point - GeoJSON point object
 * @param {Object} startPointSource - OpenLayers source for start points
 * @param {string} [domain="project"] - Domain ("project" or "scenario")
 * @param {string} [type="standard"] - Type ("temp", "ghost", or "standard")
 */
export function addStartPoint(
  point,
  startPointSource,
  domain = "project",
  type = "standard"
) {
  const format = new GeoJSON();
  const feature = format.readFeature({
    type: "Feature",
    geometry: point
  });
  feature.set("name", "StartPoint");
  feature.set("domain", domain);

  const projectedFeature = reprojectFeature(feature);

  // If type is "temp" get all features from the source
  // and remove only the "temp" types
  const existingFeatures = startPointSource.getFeatures();

  if (type === "temp") {
    existingFeatures.forEach(f => {
      if (f.get("type") === "temp") {
        startPointSource.removeFeature(f);
      }
      if (f.get("domain") === domain && f.get("name") === "StartPoint") {
        startPointSource.removeFeature(f);
      }
    });
    // Set the new feature's type to "temp" and add it to the source
    projectedFeature.set("type", "temp");
  } else if (type === "ghost") {
    projectedFeature.set("type", "ghost");
  } else {
    startPointSource.clear(true);
  }
  startPointSource.addFeature(projectedFeature);
}

/**
 * Adds an end point to the specified OpenLayers source.
 * @param {Object} point - GeoJSON point object
 * @param {Object} endPointSource - OpenLayers source for end points
 * @param {string} [domain="project"] - Domain ("project" or "scenario")
 * @param {string} [type="standard"] - Type ("temp", "ghost", or "standard")
 */
export function addEndPoint(
  point,
  endPointSource,
  domain = "project",
  type = "standard"
) {
  console.log("domain: ", domain);
  console.log("type: ", type);
  const format = new GeoJSON();
  const feature = format.readFeature({
    type: "Feature",
    geometry: point
  });
  feature.set("name", "EndPoint");
  feature.set("domain", domain);

  const projectedFeature = reprojectFeature(feature);

  // If mode is "temp" get all features from the source
  // and remove only the "temp" types
  const existingFeatures = endPointSource.getFeatures();

  if (type === "temp") {
    existingFeatures.forEach(f => {
      if (f.get("type") === "temp") {
        endPointSource.removeFeature(f);
      }
      if (f.get("domain") === domain && f.get("name") === "EndPoint") {
        endPointSource.removeFeature(f);
      }
    });
    // Set the new feature's type to "temp" and add it to the source
    projectedFeature.set("type", "temp");
  } else if (type === "ghost") {
    projectedFeature.set("type", "ghost");
  } else {
    endPointSource.clear(true);
  }
  endPointSource.addFeature(projectedFeature);
}

/**
 * Commits temporary points to permanent by removing temp type.
 * @param {Object} startPointSource - OpenLayers source for start points
 * @param {Object} endPointSource - OpenLayers source for end points
 */
export function commitTempPoints(startPointSource, endPointSource) {
  // An array of your start and end point sources
  const sources = [startPointSource, endPointSource];

  sources.forEach(source => {
    if (!source) return;

    const features = source.getFeatures();
    // Find the temporary "temp" feature in the source
    const tempFeature = features.find(f => f.get("type") === "temp");

    // If a temp feature exists, proceed
    if (tempFeature) {
      // Find all permanent features (those not marked as "temp")
      const permanentFeaturesToRemove = features.filter(
        f => f.get("type") !== "temp"
      );

      // Remove the old permanent features
      permanentFeaturesToRemove.forEach(f => source.removeFeature(f));

      // Make the temp feature permanent by removing its "type" property
      tempFeature.unset("type");
    }
  });
}

/**
 * Clears temporary points from start and end point sources.
 * @param {Object} startPointSource - OpenLayers source for start points
 * @param {Object} endPointSource - OpenLayers source for end points
 */
export function clearTempPoints(startPointSource, endPointSource) {
  // Get the features from the start point source
  if (startPointSource) {
    const startPointFeatures = startPointSource?.getFeatures();
    if (startPointFeatures) {
      startPointFeatures.forEach(f => {
        if (f.get("type") === "temp") {
          startPointSource.removeFeature(f);
        }
      });
    }
  }
  // Get the features from the end point source
  if (endPointSource) {
    const endPointFeatures = endPointSource?.getFeatures();
    if (endPointFeatures) {
      endPointFeatures.forEach(f => {
        if (f.get("type") === "temp") {
          endPointSource.removeFeature(f);
        }
      });
    }
  }
}

/**
 * Adds a project area to the specified OpenLayers source and zooms to it.
 * @param {Object} map - OpenLayers map instance
 * @param {Object} areaFeature - GeoJSON area feature (Polygon/MultiPolygon)
 * @param {Object} areaSource - OpenLayers source for area features
 * @param {string} mode - Mode of the feature ("temp" or other)
 * @param {boolean} fitView - If false, skips fitting the view to the area
 */
export function addProjectArea(
  map,
  areaFeature,
  areaSource,
  mode,
  fitView = true
) {
  const format = new GeoJSON();
  const feature = format.readFeature({
    type: "Feature",
    geometry: areaFeature,
    properties: { name: "ProjectArea" }
  });
  const projectedFeature = reprojectFeature(feature);

  // Clear any existing "temp" area
  const existingFeatures = areaSource.getFeatures();
  existingFeatures.forEach(f => {
    if (f.get("type") === "temp") {
      areaSource.removeFeature(f);
    }
  });

  // Add the new "temp" area
  if (mode === "temp") {
    projectedFeature.set("type", "temp");
  }
  areaSource.addFeature(projectedFeature);

  // Zoom to the added feature
  if (fitView) {
    const extent = projectedFeature.getGeometry().getExtent();
    const view = map.getView();

    // Fit the view to the feature's extent with some padding
    view.fit(extent, {
      padding: [50, 50, 50, 50], // Padding around the feature
      maxZoom: 18, // Maximum zoom level
      duration: 500 // Animation duration in milliseconds
    });
  }
}

/**
 * Clears temporary project area from the specified source.
 * @param {Object} areaSource - OpenLayers source for area features
 */
export function clearProjectTempArea(areaSource) {
  // Get the features from the start point source
  const areaPointFeatures = areaSource?.getFeatures();
  if (areaPointFeatures) {
    areaPointFeatures.forEach(f => {
      if (f.get("type") === "temp") {
        areaSource.removeFeature(f);
      }
    });
  }
}

// Number of vertices used to approximate the ellipse outline.
const ELLIPSE_SEGMENTS = 64;

/**
 * Builds a project-area polygon from the project Start and End points, which
 * act as the foci of an ellipse, plus a buffer and circuity percentage.
 *
 * See https://gitlab.com/stefano.g/pathfinder_dev/-/work_items/4749 for reference.
 *
 * @param {Object} startPoint - GeoJSON Point `{ type, coordinates: [lon, lat] }` (EPSG:4326)
 * @param {Object} endPoint - GeoJSON Point `{ type, coordinates: [lon, lat] }` (EPSG:4326)
 * @param {number} bufferPct - Buffer percentage.
 * @param {number} circuityPct - Circuity percentage.
 * @returns {Object} GeoJSON Polygon `{ type: "Polygon", coordinates: [[...]] }` in EPSG:4326.
 */
export function buildEllipseArea(startPoint, endPoint, bufferPct, circuityPct) {
  // Reproject both points to map projection (EPSG:3857, meters) so we can do
  // simple planar geometry.
  const start = reprojectPointAsCoords(new Point(startPoint.coordinates));
  const end = reprojectPointAsCoords(new Point(endPoint.coordinates));

  // Center on the midpoint
  const center = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];

  // Apply shape parameters
  const d = (1.0 + bufferPct / 100.0) * Math.sqrt(dx * dx + dy * dy);
  const lmax = d * (1.0 + circuityPct / 100.0);
  const w = Math.sqrt(lmax * lmax - d * d);

  // Ellipse rotation parameters
  const orientation = Math.atan2(dy, dx);
  const cos_orient = Math.cos(orientation);
  const sin_orient = Math.sin(orientation);

  // Build the ring in EPSG:3857 then unproject each vertex back to EPSG:4326.
  const ring = [];
  for (let i = 0; i < ELLIPSE_SEGMENTS; i++) {
    const angle = (2 * Math.PI * i) / ELLIPSE_SEGMENTS;
    const x = 0.5 * d * Math.cos(angle);
    const y = 0.5 * w * Math.sin(angle);
    const coord3857 = [
      center[0] + x * cos_orient + y * sin_orient,
      center[1] + x * sin_orient - y * cos_orient
    ];
    ring.push(convertTo4326(coord3857));
  }

  // Close the ring with an exact copy of the first vertex: recomputing it at
  // angle 2*PI leaves a floating-point residue that GEOS rejects as an
  // unclosed ring.
  ring.push(ring[0]);

  return {
    type: "Polygon",
    coordinates: [ring]
  };
}

/**
 * Adds an intermediate point to the specified OpenLayers source.
 * @param {Object} point - GeoJSON point object
 * @param {number} idx - Index of the intermediate point
 * @param {Object} source - OpenLayers source for intermediate points
 * @param {string} mode - Mode of the feature ("temp" or other)
 */
export function addIntermediatePoint(point, idx, source, mode) {
  const format = new GeoJSON();
  const feature = format.readFeature({
    type: "Feature",
    geometry: point
  });

  feature.set("name", "IntermediatePoint");
  feature.set("markerID", (idx + 1).toString());

  const projectedFeature = reprojectFeature(feature);

  if (mode === "temp") {
    // Set the new feature's type to "temp" and add it to the source
    projectedFeature.set("type", "temp");
  } else {
    source.clear(true);
  }
  source.addFeature(projectedFeature);
}

/**
 * Clears temporary intermediate points from the specified source.
 * @param {Object} source - OpenLayers source for intermediate points
 */
export function clearTempIntermediatePoints(source) {
  // Get the features from the start point source
  const features = source?.getFeatures();
  if (features) {
    features.forEach(f => {
      if (f.get("type") === "temp") {
        source.removeFeature(f);
      }
    });
  }
}

/**
 * Clears all highlighted points from the specified source.
 * @param {Object} source - OpenLayers source for highlighted points
 */
export function clearHighlightedPoints(source) {
  source.clear();
}

/**
 * Adds a highlighted circle point at the specified coordinates.
 * @param {number} lon - Longitude of the point
 * @param {number} lat - Latitude of the point
 * @param {Object} source - OpenLayers source for highlighted points
 * @param {Object} map - OpenLayers map instance
 */
export function addHighlightedPoint(lon, lat, source, map) {
  const highlightCircle = olCircleInMeters(map, lon, lat, 60);
  source.addFeature(highlightCircle);
}

/**
 * Calculates raster dimensions (width, height in pixels) from a geometry's extent.
 * @param {Object} geometry - OpenLayers geometry object (e.g., Polygon, Circle)
 * @param {number} [rasterResolution=10] - Resolution in map units per pixel
 * @returns {Object} Object with calculated width and height
 */
export function calculateRasterDimensions(geometry, rasterResolution = 10) {
  if (!geometry) {
    return { width: 0, height: 0 };
  }

  const extent = geometry.getExtent();

  const width = Math.round((extent[2] - extent[0]) / rasterResolution + 0.5);
  const height = Math.round((extent[3] - extent[1]) / rasterResolution + 0.5);
  return { width, height };
}

/**
 * Adds a Street View pointer to the map. It clears all previous features
 * and after adding the pointer it zooms to it.
 *
 * @param {Object} map - OpenLayers map instance
 * @param {Object} point - Point for the Street View pointer
 * @param {Object} source - OpenLayers source for highlighted points
 */
export function addStreetViewPoint(map, point, source) {
  const format = new GeoJSON();
  const feature = format.readFeature({
    type: "Feature",
    geometry: point
  });
  feature.set("name", "StreetViewPointer");

  const projectedFeature = reprojectFeature(feature);

  source.clear();

  source.addFeature(projectedFeature);

  // Zoom to the added feature
  const extent = projectedFeature.getGeometry().getExtent();
  const view = map.getView();

  // Fit the view to the feature's extent with some padding
  view.fit(extent, {
    padding: [50, 50, 50, 50], // Padding around the feature
    maxZoom: 18, // Maximum zoom level
    duration: 500 // Animation duration in milliseconds
  });
}

/**
 * Adds a geocoder search point to the map and animates the view to it.
 * @param {Array<number>} coordinate - The coordinate [x, y] in map projection
 * @param {Object} source - OpenLayers source for geocoder points
 * @param {Object} map - OpenLayers map instance
 */
export function addGeocoderPoint(coordinate, source, map) {
  // Clear any existing geocoder points
  source.clear(true);

  // Create and add the new point feature
  const pointFeature = new Feature(new Point(coordinate));
  source.addFeature(pointFeature);

  // Animate the view to the geocoder point
  const view = map.getView();
  const zoom = view.getZoom();

  view.animate({
    center: coordinate,
    duration: 2000
  });

  view.animate(
    {
      zoom: zoom - 2,
      duration: 1000
    },
    {
      zoom: 15,
      duration: 1000
    }
  );
}

/**
 * Clears all geocoder search points from the map.
 * @param {Object} source - OpenLayers source for geocoder points
 * @returns {boolean} True if points were cleared, false if no points to clear
 */
export function clearGeocoderPoints(source) {
  if (source) {
    const features = source.getFeatures();
    if (features.length > 0) {
      source.clear(true);
      return true;
    }
  }
  return false;
}
